/**
 * motionTokens — typed shared variants for `motion` (formerly framer-motion).
 *
 * Single source of truth for durations, easings, and reusable variant
 * objects so animated surfaces across `@csk/demo-ui` stay tonally
 * consistent without each component re-deriving its own numbers.
 *
 * Naming convention:
 *   - `motionDurations.<role>` — seconds (matches `motion`'s `transition.duration` API).
 *   - `motionEasings.<role>` — cubic-bezier arrays or named curves accepted by `motion`.
 *   - `<surface>Variants` — fully-typed `Variants` objects passed to `motion.*` components.
 *
 * Every consumer is expected to honor `prefers-reduced-motion`. The
 * canonical pattern is to call `useReducedMotion()` from `motion/react`
 * and short-circuit to a static render (or `initial: false`) when the
 * media query is set; see `WitnessEnter` / `ProofStepReveal` /
 * `ModeTransition` for examples.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import type { Variants } from "motion/react";
import motionTokenData from "./motion-tokens.data.json" with { type: "json" };

type CubicBezier = readonly [number, number, number, number];

interface MotionTokenData {
  readonly durations: {
    readonly burst: number;
    readonly flash: number;
    readonly enter: number;
    readonly exit: number;
    readonly stagger: number;
    readonly morph: number;
  };
  readonly easings: {
    readonly emphasized: CubicBezier;
    readonly standard: CubicBezier;
    readonly decelerate: CubicBezier;
    readonly ringExpand: "easeOut";
  };
}

const MOTION_TOKEN_DATA = motionTokenData as unknown as MotionTokenData;

/**
 * Canonical animation durations in seconds.
 *
 * Tuned against Material 3 standard / emphasized timings, biased
 * slightly faster because the demo surfaces are dense.
 */
export const motionDurations = MOTION_TOKEN_DATA.durations;

/**
 * Canonical easing curves. Where `motion` accepts a cubic-bezier array,
 * we ship the array; for the simple cases (`easeOut`, `easeInOut`) we
 * ship `motion`'s named easings so the type stays in `motion`'s vocabulary.
 */
export const motionEasings = MOTION_TOKEN_DATA.easings;

export const easingFunctions = {
  easeInQuad: (t: number): number => t * t,
  easeOutQuad: (t: number): number => 1 - (1 - t) * (1 - t),
  easeOutCubic: (t: number): number => 1 - (1 - t) ** 3,
  easeInOutCubic: (t: number): number => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2),
} as const;

/**
 * Generic entrance / exit for cards, banners, and badges.
 *
 * Per-variant transitions are baked in so consumers don't need to
 * re-derive the canonical enter / exit timing per call-site. The
 * `enter` variant uses `motionDurations.enter` + the decelerate
 * curve (slow-out feels like an arrival); the `exit` variant uses
 * the faster `motionDurations.exit` (dismissals stay snappy).
 */
export const entranceVariants: Variants = {
  initial: { opacity: 0, y: 8 },
  enter: {
    opacity: 1,
    y: 0,
    transition: {
      duration: motionDurations.enter,
      ease: motionEasings.decelerate,
    },
  },
  exit: {
    opacity: 0,
    y: -4,
    transition: {
      duration: motionDurations.exit,
      ease: motionEasings.standard,
    },
  },
};

/** Per-item variant used inside a staggered reveal sequence. */
export const stepVariants: Variants = {
  initial: { opacity: 0, y: 4 },
  enter: {
    opacity: 1,
    y: 0,
    transition: {
      duration: motionDurations.enter,
      ease: motionEasings.decelerate,
    },
  },
};

/** Container variant that drives `stagger` on its children. */
export const stepContainerVariants: Variants = {
  initial: {},
  enter: {
    transition: {
      staggerChildren: motionDurations.stagger,
    },
  },
};

/** NeutralizationBurst's expanding ring. */
export const burstRingVariants: Variants = {
  initial: { scale: 0.2, opacity: 0.55 },
  expand: { scale: 2.6, opacity: 0 },
};

/** NeutralizationBurst's radial flash overlay. */
export const burstFlashVariants: Variants = {
  initial: { opacity: 0.35 },
  fade: { opacity: 0 },
};
